// Client for the admin-gated PostHog geo function (netlify/functions/
// posthog-geo.js). Sends the caller's Firebase ID token so the function
// verifies admin access server-side. Returns null on any failure so callers
// render gracefully (the UI just omits geo).
import { auth } from "../firebase/firebaseConfig";

const FN = "/.netlify/functions/posthog-geo";

async function authedGet(qs) {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdToken();
    const res = await fetch(`${FN}?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error("posthog-geo fetch failed:", err);
    return null;
  }
}

// Single user: { geo: { city, region, country, ip } }
// `until` (ISO date, e.g. a deletion) looks back from then instead of now.
export function getGeo(uid, until) {
  if (!uid) return Promise.resolve(null);
  return authedGet(`uid=${encodeURIComponent(uid)}${until ? `&until=${encodeURIComponent(until)}` : ""}`);
}

// Recent App Store review-prompt events across all users:
// { reviews: [{uid,event,created_at,trigger,reason}] }
export function getReviews(days) {
  return authedGet(`reviews=1${days ? `&days=${days}` : ""}`);
}

// Recent real PostHog events for a user: { activity: [{event_type,created_at,venue_name,query,source}] }
export function getActivity(uid, { until, days } = {}) {
  if (!uid) return Promise.resolve(null);
  const extra = `${until ? `&until=${encodeURIComponent(until)}` : ""}${days ? `&days=${days}` : ""}`;
  return authedGet(`activity=${encodeURIComponent(uid)}${extra}`);
}

// Batch latest IP+geo per firebase uid: { data: { <uid>: {ip,city,region,country} } }
// Asked in groups of 10 so each call fits the function's time limit; a slow group
// only leaves its own rows blank.
export async function getIps(uids) {
  const list = (uids || []).filter(Boolean);
  if (!list.length) return null;
  const groups = [];
  for (let i = 0; i < list.length; i += 10) groups.push(list.slice(i, i + 10));
  const parts = await Promise.all(groups.map((g) => authedGet(`ips=${g.map(encodeURIComponent).join(",")}`)));
  const data = Object.assign({}, ...parts.map((p) => p?.data || {}));
  return { data };
}

// Anonymous users (opened the app, never signed up):
// { window_days, total, last7, returned, users: [{device,city,region,country,first_seen,last_seen,events}] }
export function getAnonymousUsers(days) {
  return authedGet(`anonymous=1${days ? `&days=${days}` : ""}`);
}

// "Miami, FL" style label from a geo object.
export function geoLabel(geo) {
  if (!geo) return null;
  const parts = [geo.city, geo.region].filter(Boolean);
  if (!parts.length && geo.country) return geo.country;
  return parts.join(", ") || null;
}
